<template>
  <div class="puff-color-selector" :class="{ 'puff-color-selector--with-border': showBorderUI }">
    <template v-if="label">
      <StepNumber v-if="showBorderUI" step-number="2" :content="label"></StepNumber>
      <div v-else class="puff-color-selector__label">{{ label }}</div>
    </template>
    <div class="puff-color-selector__items">
      <div
        v-for="color in colors"
        :key="color.label"
        :class="{
          'puff-color-selector__item': true,
          'puff-color-selector__item--selected': artwork.color === color.value
        }"
        @click="handleSelect(color.value)"
      >
        <div :style="{ backgroundColor: color.value }" class="puff-color-selector__color">
          <UseSvg
            v-if="artwork.color === color.value"
            icon="checkbox"
            width="40"
            height="40"
            :class="[
              'puff-color-selector__checkbox',
              `puff-color-selector__checkbox--${color.label}`
            ]"
          />
        </div>
        {{ color.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from "vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import StepNumber from "./common/StepNumber.vue";
import { useDtfStore } from "./store";

const colors = [
  { label: "white", value: "#FFFFFF" },
  { label: "black", value: "#000000" },
  { label: "red", value: "#D50132" },
  { label: "orange", value: "#FA4617" },
  { label: "gold", value: "#FEB414" },
  { label: "yellow", value: "#FFDD04" },
  { label: "green", value: "#007A00" },
  { label: "blue", value: "#1261A9" },
  { label: "purple", value: "#432F92" },
  { label: "teal", value: "#008080" },
  { label: "mint", value: "#B6E4E4" },
  { label: "lavender", value: "#C7B1EA" },
  { label: "coral", value: "#FDA398" },
  { label: "pink", value: "#EE529C" },
  { label: "denim", value: "#1C5C82" },
  { label: "brown", value: "#4D3525" }
];

defineProps({
  label: [String, Number]
});

const store = useDtfStore();
const artwork = computed(() => store.artworks[0]);
const showBorderUI = computed(() => store.showStepsUI);

const handleSelect = color => {
  store.changePuffColor({ color });
};

watch(
  () => artwork.value.url,
  url => {
    if (!artwork.value.isFile) {
      const color = artwork.value.color || colors[0].value;
      handleSelect(url ? color : null);
    }
  }
);

watch(
  () => artwork.value.enhancementTier,
  () => {
    const color = artwork.value.color;
    if (color) {
      handleSelect(color);
    }
  }
);
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";

.puff-color-selector {
  margin-top: 24px;

  &--with-border {
    border: 1px solid $gray-border;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0px 25px 30px 0px rgba($black, 0.05);
    background-color: $white;
    margin: 16px 0;
  }

  &__items {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__label {
    margin: 0 0 16px;
    color: $text-black;
    font-size: 16px;
    font-weight: 700;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 0 4px;
    text-align: center;
    cursor: pointer;
    border-radius: 4px;
    border: 1px solid $gray-border;
    background: $white;
    overflow: hidden;
    width: 72px;
    color: $text-black;
    font-size: 13px;
    font-weight: 400;
    text-transform: capitalize;

    &--selected {
      outline: 1px solid $bright-pink;
      border-color: $bright-pink;
    }
  }

  &__color {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;

    @include with-tablet {
      height: 72px;
    }
  }

  &__checkbox {
    color: $white;

    &--white {
      color: $black;
    }
  }
}
</style>
